import { c as _c } from "react/compiler-runtime";
// ── AccessDeniedState — the ONE access-denied surface ────────────────────────
// Every permission/role/workspace denial renders this: Lock icon, fixed title,
// no retry (a retry cannot grant access), no Support ID. Callers may pass a
// human `description` (what is missing); it must never be a raw permission
// string. Hook-free so tests can invoke it as a plain function. Ref: ASC-11221

import type { ReactElement } from 'react';
import { StateDisplay } from '@oraczen/zendesign-ui/components/state-display';
import { Lock } from 'lucide-react';
import { toErrorPresentation } from './toErrorPresentation';

/** Single source of denial copy — wrappers override only the description. */
export const ACCESS_DENIED_COPY = {
  title: 'Access Restricted',
  description: "You don't have permission to access this. Contact your administrator for access."
} as const;
export interface AccessDeniedStateProps {
  /** Human wording for what is restricted; defaults to `ACCESS_DENIED_COPY.description`. */
  description?: string;
  /** Passthrough to StateDisplay; omit to inherit its default (`true`). */
  isBordered?: boolean;
  /** Sizing/centering is the caller's job, same contract as `ErrorState`. */
  className?: string;
}
export function AccessDeniedState(t0) {
  const $ = _c(4);
  const {
    description: t1,
    isBordered,
    className
  } = t0;
  const description = t1 === undefined ? ACCESS_DENIED_COPY.description : t1;
  let t2;
  if ($[0] !== className || $[1] !== description || $[2] !== isBordered) {
    t2 = <StateDisplay variant="empty" icon={Lock} title={ACCESS_DENIED_COPY.title} description={description} isBordered={isBordered} className={className} />;
    $[0] = className;
    $[1] = description;
    $[2] = isBordered;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/**
 * True for a 403 (even with no errorzen code) or an `ACCESS.*` / `AUTH.FORBIDDEN`
 * code. A 403 is an access-denied surface, never a retryable ErrorState.
 */
export function isAccessDeniedError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'status' in error && (error as {
    status?: unknown;
  }).status === 403) {
    return true;
  }
  const code = toErrorPresentation(error).code;
  return code === 'AUTH.FORBIDDEN' || (code?.startsWith('ACCESS.') ?? false);
}